import styled from 'styled-components';
import { forwardRef, ReactNode } from 'react';
import { PageBodyWrapper } from './PageBody';
import {
  HorizontalResizableContainerWrapper,
  ResizableHorizontal,
} from '@ardoq/resizable';
import { BackgroundColor, FlexBox } from '@ardoq/layout';

type PageBodyWrapperProps = {
  preventScroll?: boolean;
  backgroundColor?: BackgroundColor;
};

const SIDE_CONTAINER_MIN_WIDTH = 255;
const SideContainer = styled(FlexBox).attrs({
  gap: 'medium',
})`
  height: 100%;
`;

// Require at least left or right content
type DefinedReactNode = Exclude<ReactNode, undefined>;
type SideContentProps =
  | {
      leftContent: DefinedReactNode;
      rightContent?: never;
    }
  | {
      leftContent?: never;
      rightContent: DefinedReactNode;
    }
  | {
      leftContent: DefinedReactNode;
      rightContent: DefinedReactNode;
    };

type ResizablePageBodyProps = {
  children: ReactNode;
  headerContent?: ReactNode;
  leftContentInitialWidth?: number;
  leftContentMinWidth?: number;
  rightContentInitialWidth?: number;
  rightContentMinWidth?: number;
  /**
   * If true, padding will not be applied to the body.
   * You may need it if you want to embed a page body inside another page body.
   */
  skipPadding?: boolean;
  footerContent?: ReactNode;
} & PageBodyWrapperProps &
  SideContentProps;

export const ResizablePageBody = forwardRef<
  HTMLDivElement,
  ResizablePageBodyProps
>(
  (
    {
      children,
      leftContent,
      leftContentInitialWidth = SIDE_CONTAINER_MIN_WIDTH,
      leftContentMinWidth = SIDE_CONTAINER_MIN_WIDTH,
      rightContent,
      rightContentInitialWidth = SIDE_CONTAINER_MIN_WIDTH,
      rightContentMinWidth = SIDE_CONTAINER_MIN_WIDTH,
      headerContent,
      preventScroll,
      backgroundColor,
      skipPadding,
      footerContent,
    },
    ref
  ) => (
    <FlexBox overflow="hidden" flex={1} flexDirection="column" ref={ref}>
      <FlexBox overflow="hidden" flex={1}>
        <ResizableHorizontal hasHorizontalScroll>
          {leftContent ? (
            <HorizontalResizableContainerWrapper
              key="leftSide"
              minWidth={leftContentMinWidth}
              initialWidth={leftContentInitialWidth}
            >
              <SideContainer>{leftContent}</SideContainer>
            </HorizontalResizableContainerWrapper>
          ) : (
            <HorizontalResizableContainerWrapper
              key="leftSide"
              isCollapsed
              initialWidth={0}
            />
          )}
          <HorizontalResizableContainerWrapper>
            <FlexBox
              flexDirection="column"
              key="body"
              flex={1}
              height="full"
              style={{ overflowY: 'auto' }}
            >
              {headerContent}
              <PageBodyWrapper
                $skipPadding={skipPadding}
                $preventScroll={preventScroll}
                $backgroundColor={backgroundColor}
              >
                {children}
              </PageBodyWrapper>
            </FlexBox>
          </HorizontalResizableContainerWrapper>
          {rightContent ? (
            <HorizontalResizableContainerWrapper
              key="rightSide"
              minWidth={rightContentMinWidth}
              initialWidth={rightContentInitialWidth}
            >
              <SideContainer> {rightContent}</SideContainer>
            </HorizontalResizableContainerWrapper>
          ) : (
            <HorizontalResizableContainerWrapper
              key="rightSide"
              isCollapsed
              initialWidth={0}
            />
          )}
        </ResizableHorizontal>
      </FlexBox>
      {footerContent}
    </FlexBox>
  )
);
